import {useEffect, useCallback, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {selectFilterConfig} from '../slices/configSlice'
import {selectFilters} from '../slices/breadCrumbsSlice'
import {
  applyBusinessRules,
  setAvailableFilters,
  setSelectedState as setSelectedStateAction,
  selectDisabledFilters,
  selectAvailableFilters,
  selectBenefitsEligibility,
  updateFiltersOnStateChange
} from '../slices/filterManagementSlice'
import {getDisabledFilterTooltip} from '../../utils/filterManagementHelpers'
import tooltipTexts from '../../components/common/TooltipText.json'

/**
 * Custom hook to handle business logic for filter management
 *
 * This hook encapsulates all the business rules for:
 * - State-specific filter visibility
 * - Conditional filter dependencies
 * - Dynamic filter disabling/enabling
 * - Permission-based filter availability
 *
 * Usage: Call this hook at the top level of components that need filter business logic
 */
export const useFilterBusinessLogic = () => {
  const dispatch = useDispatch()
  const filterConfig = useSelector(selectFilterConfig)
  const breadcrumbFilters = useSelector(selectFilters)
  const disabledFilters = useSelector(selectDisabledFilters)
  const availableFilters = useSelector(selectAvailableFilters)
  const benefitsEligibility = useSelector(selectBenefitsEligibility)

  // Eligibility filters disabled when corresponding outreach benefits are selected
  const outreachDisabledFilters = useMemo(() => {
    const disabled = []
    if (benefitsEligibility?.selected?.includes('SNAP')) {
      disabled.push('usdaSnapInd')
    }
    if (benefitsEligibility?.selected?.includes('Medicaid')) {
      disabled.push('medicaidEligInd')
    }
    return disabled
  }, [benefitsEligibility?.selected])

  const stateFilter = breadcrumbFilters?.[0]
  const selectedStateArray = useMemo(() => stateFilter?.default || [], [stateFilter?.default])

  const isNationalView = useMemo(
    () =>
      selectedStateArray.length > 0 &&
      (selectedStateArray.includes('All States') || selectedStateArray.includes(filterConfig?.entireUnitedStateLabel)),
    [selectedStateArray, filterConfig?.entireUnitedStateLabel]
  )

  const hasSpecificStates = useMemo(
    () => !isNationalView && selectedStateArray.length > 0,
    [isNationalView, selectedStateArray]
  )

  const selectedState = useMemo(
    () => (selectedStateArray.length > 0 ? selectedStateArray[0] : null),
    [selectedStateArray]
  )

  useEffect(() => {
    dispatch(setSelectedStateAction(selectedState))
  }, [selectedState, dispatch])

  useEffect(() => {
    if (!filterConfig) return
    dispatch(updateFiltersOnStateChange(selectedStateArray, filterConfig))
  }, [selectedStateArray, filterConfig, dispatch])

  /**
   * Update available filters from API permissions
   * Call this when you receive permission data from the API
   * Similar to how SDOH gets sdohKeys from filterDetails API
   *
   * @param {Array} permissionsData - Array of filter permission objects with FILTER_KEY
   * @example
   * const {filterDetails} = useFilterListDetail()
   * updateAvailableFiltersFromAPI(filterDetails?.filterKeys || [])
   */
  const updateAvailableFiltersFromAPI = useCallback(
    (permissionsData) => {
      if (!permissionsData || !Array.isArray(permissionsData)) return

      const allowedFilterKeys = permissionsData
        .map((permission) => permission.FILTER_KEY || permission.key)
        .filter(Boolean)

      dispatch(setAvailableFilters(allowedFilterKeys))
    },
    [dispatch]
  )

  const isChildCareDisabled = useCallback(() => {
    if (isNationalView) return false
    if (!hasSpecificStates) return false
    if (!filterConfig?.childCareDisableState) return false
    return selectedStateArray.every((state) => filterConfig.childCareDisableState.includes(state))
  }, [selectedStateArray, isNationalView, hasSpecificStates, filterConfig])

  const areGeographicFiltersDisabled = useCallback(() => {
    if (!filterConfig?.disableCommunitySupportAndLocationFiltersKey) return false
    return isNationalView
  }, [isNationalView, filterConfig])

  const isFilterDisabled = useCallback(
    (filterKey) => {
      return disabledFilters.includes(filterKey) || outreachDisabledFilters.includes(filterKey)
    },
    [disabledFilters, outreachDisabledFilters]
  )

  const isFilterAvailable = useCallback(
    (filterKey) => {
      if (availableFilters.length === 0) return true
      return availableFilters.includes(filterKey)
    },
    [availableFilters]
  )

  const getDisabledReason = useCallback(
    (filterKey) => {
      if (outreachDisabledFilters.includes(filterKey)) {
        return tooltipTexts.benefitsAlreadySelected
      }

      if (!disabledFilters.includes(filterKey)) {
        if (availableFilters.length > 0 && !availableFilters.includes(filterKey)) {
          return tooltipTexts.filterDisabled
        }
        return null
      }

      return getDisabledFilterTooltip(filterKey, selectedStateArray, filterConfig)
    },
    [disabledFilters, availableFilters, filterConfig, selectedStateArray, outreachDisabledFilters]
  )

  const applyAllBusinessRules = useCallback(
    (state) => {
      dispatch(
        applyBusinessRules({
          selectedState: state,
          config: filterConfig
        })
      )
    },
    [dispatch, filterConfig]
  )

  const checkFilterDependencies = useCallback(
    (filterKey) => {
      if (!filterConfig?.filterDependencies?.[filterKey]) {
        return {met: true, missingDependencies: []}
      }

      const dependencies = filterConfig.filterDependencies[filterKey]
      const missingDependencies = []

      for (const dep of dependencies) {
        if (dep === 'state') {
          if (!selectedState || selectedState === filterConfig?.entireUnitedStateLabel) {
            missingDependencies.push('state')
          }
        }
      }

      return {
        met: missingDependencies.length === 0,
        missingDependencies
      }
    },
    [filterConfig, selectedState]
  )

  return {
    selectedState,
    disabledFilters,
    availableFilters,
    outreachDisabledFilters,
    isChildCareDisabled,
    areGeographicFiltersDisabled,
    isFilterDisabled,
    isFilterAvailable,
    getDisabledReason,
    checkFilterDependencies,
    applyAllBusinessRules,
    updateAvailableFiltersFromAPI
  }
}

export default useFilterBusinessLogic
